/**
 * Project Service
 * Handles all project-related database operations with Supabase
 */

import { supabase, STORAGE_BUCKET } from '@/lib/supabase';
import { Project, RoomType, DbProject, DbImage, GenerationType, GalleryImage, AspectRatio, StagingStyle, HeroOverlayConfig } from '@/types';
import { convertHeicToJpeg } from '@/services/heicConversion';

// ============================================================================
// Project Operations
// ============================================================================

/**
 * Fetch all projects for the current user with thumbnail
 * Note: RLS handles access control (own projects + team admin's projects)
 * The userId parameter is kept for backwards compatibility but RLS uses auth.uid()
 */
export const fetchProjects = async (_userId: string): Promise<Project[]> => {
  // Let RLS handle the filtering - it will return:
  // 1. User's own projects (where user_id = auth.uid())
  // 2. Projects of their team admin (where is_team_member_of(user_id))
  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(`Projekte konnten nicht geladen werden: ${error.message}`);
  }

  // Fetch thumbnails for each project
  const projects = await Promise.all(
    (data as DbProject[]).map(async (project) => {
      const thumbnail = await getProjectThumbnail(project.id);
      return {
        ...mapDbProjectToProject(project),
        thumbnailUrl: thumbnail,
      };
    })
  );

  return projects;
};

/**
 * Fetch project member assignments (projectId → memberIds map)
 */
export const fetchProjectAssignments = async (): Promise<Record<string, string[]>> => {
  const { data, error } = await supabase
    .from('project_members')
    .select('project_id, member_id');

  if (error) {
    console.error('[Project] Failed to load assignments:', error.message);
    return {};
  }

  const map: Record<string, string[]> = {};
  for (const row of data || []) {
    if (!map[row.project_id]) map[row.project_id] = [];
    map[row.project_id].push(row.member_id);
  }
  return map;
};

/**
 * Get thumbnail URL for a project (first generated image)
 */
export const getProjectThumbnail = async (projectId: string): Promise<string | undefined> => {
  const { data } = await supabase
    .from('images')
    .select('*')
    .eq('project_id', projectId)
    .limit(1)
    .single();

  if (data) {
    // Prefer branded > branded_empty > upscaled_staged > upscaled_empty > staged > empty > original
    const path = data.storage_path_branded || data.storage_path_branded_empty || data?.storage_path_upscaled_staged || data?.storage_path_upscaled_empty || data.storage_path_staged || data.storage_path_empty || data.storage_path;
    if (path) {
      const { data: urlData } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path);
      return urlData.publicUrl;
    }
  }

  return undefined;
};

/**
 * Create a new project
 */
export const createProject = async (
  userId: string,
  name: string,
  street: string,
  aspectRatio: AspectRatio = '4:3',
  stagingStyle: StagingStyle = 'modern',
  propertyDetails?: {
    propertyType?: string;
    propertyAreaSqm?: number;
    propertyRooms?: number;
    propertyPrice?: string;
    propertyStatus?: string;
  }
): Promise<Project> => {
  // Try to create with aspect_ratio first
  let data;
  let error;

  ({ data, error } = await supabase
    .from('projects')
    .insert({
      user_id: userId,
      name,
      street,
      aspect_ratio: aspectRatio,
      staging_style: stagingStyle,
      ...(propertyDetails?.propertyType && { property_type: propertyDetails.propertyType }),
      ...(propertyDetails?.propertyAreaSqm && { property_area_sqm: propertyDetails.propertyAreaSqm }),
      ...(propertyDetails?.propertyRooms && { property_rooms: propertyDetails.propertyRooms }),
      ...(propertyDetails?.propertyPrice && { property_price: propertyDetails.propertyPrice }),
      ...(propertyDetails?.propertyStatus && { property_status: propertyDetails.propertyStatus }),
    })
    .select()
    .single());

  // If aspect_ratio column doesn't exist yet, retry without it
  if (error && error.message.includes('aspect_ratio')) {
    console.warn('[Project] aspect_ratio column not found, creating without it. Run migration 009_aspect_ratio.sql');
    ({ data, error } = await supabase
      .from('projects')
      .insert({
        user_id: userId,
        name,
        street,
      })
      .select()
      .single());
  }

  if (error) {
    throw new Error(`Projekt konnte nicht erstellt werden: ${error.message}`);
  }

  return mapDbProjectToProject(data as DbProject);
};

/**
 * Update a project
 */
export const updateProject = async (
  projectId: string,
  updates: {
    name?: string;
    street?: string;
    aspect_ratio?: AspectRatio;
    staging_style?: StagingStyle;
    upload_token?: string;
    is_upload_active?: boolean;
    tenant_upload_count?: number;
    property_type?: string | null;
    property_area_sqm?: number | null;
    property_rooms?: number | null;
    property_price?: string | null;
    property_status?: string | null;
  }
): Promise<void> => {
  const { error } = await supabase
    .from('projects')
    .update(updates)
    .eq('id', projectId);

  if (error) {
    throw new Error(`Projekt konnte nicht aktualisiert werden: ${error.message}`);
  }
};

/**
 * Delete a project and all associated images
 */
export const deleteProject = async (projectId: string): Promise<void> => {
  // First, get all images for this project to delete from storage
  const { data: images } = await supabase
    .from('images')
    .select('*')
    .eq('project_id', projectId);

  // Delete images from storage
  if (images && images.length > 0) {
    const paths = images.flatMap((img) =>
      [img.storage_path, img.storage_path_empty, img.storage_path_staged, img.storage_path_branded, img.storage_path_branded_empty, img.storage_path_upscaled_staged, img.storage_path_upscaled_empty, img.storage_path_hero].filter((p): p is string => !!p)
    );
    if (paths.length > 0) {
      const { error: storageError } = await supabase.storage.from(STORAGE_BUCKET).remove(paths);
      if (storageError) {
        console.warn('Storage deletion warning:', storageError.message);
      }
    }
  }

  // Delete project (images will be cascade deleted)
  const { error } = await supabase
    .from('projects')
    .delete()
    .eq('id', projectId);

  if (error) {
    throw new Error(`Projekt konnte nicht gelöscht werden: ${error.message}`);
  }
};

// ============================================================================
// Image Operations
// ============================================================================

/**
 * Upload an original image and save reference to database
 */
export const uploadProjectImage = async (
  projectId: string,
  file: File,
  roomType: RoomType,
  keepKitchen: boolean,
  customLabel?: string
): Promise<DbImage> => {
  // Always convert HEIC/HEIF to JPEG — browsers can't display them
  const safeFile = await convertHeicToJpeg(file).catch(() => file);

  // Generate unique filename
  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substring(2, 8);
  const ext = safeFile.name.split('.').pop() || 'jpg';
  const fileName = `${timestamp}-${randomStr}-original.${ext}`;
  const storagePath = `${projectId}/${fileName}`;

  // Upload to storage
  const { error: uploadError } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(storagePath, safeFile, { cacheControl: '3600', upsert: false });

  if (uploadError) {
    throw new Error(`Upload fehlgeschlagen: ${uploadError.message}`);
  }

  // Save reference in database
  const { data, error } = await supabase
    .from('images')
    .insert({
      project_id: projectId,
      storage_path: storagePath,
      room_type: roomType,
      keep_kitchen: keepKitchen,
      custom_label: customLabel,
      status: 'pending',
    })
    .select()
    .single();

  if (error) {
    // Rollback: Delete uploaded file
    await supabase.storage.from(STORAGE_BUCKET).remove([storagePath]);
    throw new Error(`Bild konnte nicht gespeichert werden: ${error.message}`);
  }

  return data as DbImage;
};

/**
 * Update image metadata (room_type, custom_label, keep_kitchen)
 */
export const updateImageMetadata = async (
  imageId: string,
  updates: { 
    room_type?: string; 
    custom_label?: string; 
    keep_kitchen?: boolean;
  }
): Promise<void> => {
  const { error } = await supabase
    .from('images')
    .update(updates)
    .eq('id', imageId);

  if (error) {
    throw new Error(`Bildmetadaten konnten nicht aktualisiert werden: ${error.message}`);
  }
};

/**
 * Delete multiple images from a project
 */
export const deleteMultipleImages = async (imageIds: string[]): Promise<void> => {
  // First, get all storage paths for these images
  const { data: images, error: fetchError } = await supabase
    .from('images')
    .select('*')
    .in('id', imageIds);

  if (fetchError) {
    throw new Error(`Bilder konnten nicht geladen werden: ${fetchError.message}`);
  }

  // Collect all storage paths to delete
  const storagePaths: string[] = [];
  images?.forEach((img) => {
    if (img.storage_path) storagePaths.push(img.storage_path);
    if (img.storage_path_empty) storagePaths.push(img.storage_path_empty);
    if (img.storage_path_staged) storagePaths.push(img.storage_path_staged);
    if (img.storage_path_branded) storagePaths.push(img.storage_path_branded);
    if (img.storage_path_branded_empty) storagePaths.push(img.storage_path_branded_empty);
    if (img.storage_path_upscaled_staged) storagePaths.push(img.storage_path_upscaled_staged);
    if (img.storage_path_upscaled_empty) storagePaths.push(img.storage_path_upscaled_empty);
    if (img.storage_path_hero) storagePaths.push(img.storage_path_hero);
  });

  // Delete from storage
  if (storagePaths.length > 0) {
    const { error: storageError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .remove(storagePaths);
    
    if (storageError) {
      console.warn('Storage deletion warning:', storageError.message);
    }
  }

  // Delete from database
  const { error: deleteError } = await supabase
    .from('images')
    .delete()
    .in('id', imageIds);

  if (deleteError) {
    throw new Error(`Bilder konnten nicht gelöscht werden: ${deleteError.message}`);
  }
};

/**
 * Save a generated image (Empty or Staged) to storage
 */
export const saveGeneratedImage = async (
  projectId: string,
  imageId: string,
  base64Data: string,
  generationType: GenerationType
): Promise<string> => {
  // Convert base64 to blob
  const base64Content = base64Data.split(',')[1];
  const byteCharacters = atob(base64Content);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: 'image/png' });

  // Generate filename
  const timestamp = Date.now();
  const suffix = generationType === GenerationType.EMPTY ? 'empty' : 'staged';
  const fileName = `${imageId}-${suffix}-${timestamp}.png`;
  const storagePath = `${projectId}/${fileName}`;

  // Upload to storage
  const { error: uploadError } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(storagePath, blob, { 
      cacheControl: '3600', 
      upsert: true,
      contentType: 'image/png'
    });

  if (uploadError) {
    throw new Error(`Upload des generierten Bildes fehlgeschlagen: ${uploadError.message}`);
  }

  // Update database record
  const updateField = generationType === GenerationType.EMPTY 
    ? 'storage_path_empty' 
    : 'storage_path_staged';

  const { error: updateError } = await supabase
    .from('images')
    .update({ 
      [updateField]: storagePath,
      status: 'completed'
    })
    .eq('id', imageId);

  if (updateError) {
    console.warn('Datenbank-Update fehlgeschlagen:', updateError.message);
  }

  // Return public URL
  const { data } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(storagePath);
  return data.publicUrl;
};

/**
 * Fetch all images for a project (raw database records)
 */
export const fetchProjectImages = async (projectId: string): Promise<DbImage[]> => {
  const { data, error } = await supabase
    .from('images')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(`Bilder konnten nicht geladen werden: ${error.message}`);
  }

  return data as DbImage[];
};

/**
 * Fetch all images for a project with public URLs (for gallery display)
 */
export const fetchProjectGalleryImages = async (projectId: string): Promise<GalleryImage[]> => {
  const { data, error } = await supabase
    .from('images')
    .select('*')
    .eq('project_id', projectId)
    .neq('room_type', 'floorplan')
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(`Bilder konnten nicht geladen werden: ${error.message}`);
  }

  return (data as DbImage[]).map((img) => ({
    id: img.id,
    roomType: img.room_type,
    customLabel: img.custom_label,
    originalUrl: img.storage_path ? getPublicUrl(img.storage_path) : undefined,
    emptyUrl: img.storage_path_empty ? getPublicUrl(img.storage_path_empty) : undefined,
    stagedUrl: img.storage_path_staged ? getPublicUrl(img.storage_path_staged) : undefined,
    brandedUrl: img.storage_path_branded ? getPublicUrl(img.storage_path_branded) : undefined,
    brandedEmptyUrl: img.storage_path_branded_empty ? getPublicUrl(img.storage_path_branded_empty) : undefined,
    upscaledStagedUrl: img.storage_path_upscaled_staged ? getPublicUrl(img.storage_path_upscaled_staged) : undefined,
    upscaledEmptyUrl: img.storage_path_upscaled_empty ? getPublicUrl(img.storage_path_upscaled_empty) : undefined,
    heroUrl: img.storage_path_hero ? getPublicUrl(img.storage_path_hero) : undefined,
    heroOverlayConfig: img.hero_overlay_config ?? null,
    createdAt: img.created_at,
    isTenantUpload: img.status === 'uploaded_by_tenant',
    stagedStoragePath: img.storage_path_staged || undefined,
    brandedStoragePath: img.storage_path_branded || undefined,
    emptyStoragePath: img.storage_path_empty || undefined,
  }));
};

/**
 * Fetch unprocessed images for Staging Tool
 * Returns images that haven't been staged yet (including tenant uploads)
 */
export const fetchUnprocessedImagesForStaging = async (projectId: string): Promise<{
  id: string;
  previewUrl: string;
  roomType: string;
  customLabel?: string;
  keepKitchen: boolean;
  dbId: string;
  storagePath: string;
  isTenantUpload: boolean;
}[]> => {
  const { data, error } = await supabase
    .from('images')
    .select('*')
    .eq('project_id', projectId)
    .neq('room_type', 'floorplan') // Exclude floor plan images
    .not('storage_path', 'is', null) // Must have an original image
    .order('created_at', { ascending: true });

  if (error) {
    throw new Error(`Bilder konnten nicht geladen werden: ${error.message}`);
  }

  return (data as DbImage[]).map((img) => ({
    id: `db-${img.id}`, // Prefix to distinguish from new uploads
    previewUrl: img.storage_path ? getPublicUrl(img.storage_path) : '',
    roomType: img.room_type || 'custom',
    customLabel: img.custom_label || undefined,
    keepKitchen: img.keep_kitchen ?? false,
    dbId: img.id,
    storagePath: img.storage_path!,
    isTenantUpload: img.status === 'uploaded_by_tenant',
  }));
};

/**
 * Fetch ALL generated images with URLs (for Overlay Tool)
 * Returns a flat list of ALL available variants (staged AND empty)
 */
export const fetchProjectGeneratedImages = async (projectId: string): Promise<{ url: string; name: string; variant: string }[]> => {
  const { data, error } = await supabase
    .from('images')
    .select('id, room_type, custom_label, storage_path_empty, storage_path_staged, storage_path_branded, storage_path_branded_empty, storage_path_floorplan_2d, storage_path_floorplan_3d')
    .eq('project_id', projectId)
    .order('created_at', { ascending: true });

  if (error) {
    throw new Error(`Bilder konnten nicht geladen werden: ${error.message}`);
  }

  const images: { url: string; name: string; variant: string }[] = [];

  (data as DbImage[]).forEach((img) => {
    const roomLabel = img.custom_label || img.room_type;
    const idSuffix = img.id.slice(-4);

    // Floorplan images have their own storage paths
    if (img.room_type === 'floorplan') {
      if (img.storage_path_floorplan_2d) {
        images.push({
          url: getPublicUrl(img.storage_path_floorplan_2d),
          name: `Grundriss_2D_${idSuffix}`,
          variant: 'floorplan_2d',
        });
      }
      if (img.storage_path_floorplan_3d) {
        images.push({
          url: getPublicUrl(img.storage_path_floorplan_3d),
          name: `Grundriss_3D_${idSuffix}`,
          variant: 'floorplan_3d',
        });
      }
      return; // Skip staged/empty checks for floorplans
    }

    // Staged variants (branded > unbranded)
    if (img.storage_path_branded) {
      images.push({
        url: getPublicUrl(img.storage_path_branded),
        name: `${roomLabel}_${idSuffix}_Staged_Branded`,
        variant: 'staged_branded',
      });
    } else if (img.storage_path_staged) {
      images.push({
        url: getPublicUrl(img.storage_path_staged),
        name: `${roomLabel}_${idSuffix}_Staged`,
        variant: 'staged',
      });
    }

    // Empty variants (branded > unbranded)
    if (img.storage_path_branded_empty) {
      images.push({
        url: getPublicUrl(img.storage_path_branded_empty),
        name: `${roomLabel}_${idSuffix}_Leer_Branded`,
        variant: 'empty_branded',
      });
    } else if (img.storage_path_empty) {
      images.push({
        url: getPublicUrl(img.storage_path_empty),
        name: `${roomLabel}_${idSuffix}_Leer`,
        variant: 'empty',
      });
    }
  });

  return images;
};

/**
 * Get public URL for a storage path
 */
export const getPublicUrl = (storagePath: string): string => {
  const { data } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(storagePath);
  return data.publicUrl;
};

/**
 * Delete an image
 */
export const deleteProjectImage = async (imageId: string, storagePaths: string[]): Promise<void> => {
  // Delete from storage
  const validPaths = storagePaths.filter(Boolean);
  if (validPaths.length > 0) {
    await supabase.storage.from(STORAGE_BUCKET).remove(validPaths);
  }

  // Delete from database
  const { error } = await supabase
    .from('images')
    .delete()
    .eq('id', imageId);

  if (error) {
    throw new Error(`Bild konnte nicht gelöscht werden: ${error.message}`);
  }
};

/**
 * Delete a specific version of an image (empty, staged, branded, or branded_empty)
 */
export const deleteImageVersion = async (
  imageId: string,
  version: 'empty' | 'staged' | 'upscaled_staged' | 'upscaled_empty' | 'branded' | 'branded_empty',
  storagePath: string
): Promise<void> => {
  // Delete from storage
  if (storagePath) {
    const { error: storageError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .remove([storagePath]);
    
    if (storageError) {
      console.warn('Storage deletion failed:', storageError.message);
    }
  }

  // Update database record to clear the path
  const updateField = `storage_path_${version}`;
  const { error } = await supabase
    .from('images')
    .update({ [updateField]: null })
    .eq('id', imageId);

  if (error) {
    throw new Error(`Version konnte nicht gelöscht werden: ${error.message}`);
  }
};

// ============================================================================
// Helpers
// ============================================================================

const mapDbProjectToProject = (dbProject: DbProject): Project => ({
  id: dbProject.id,
  userId: dbProject.user_id,
  name: dbProject.name,
  address: dbProject.street,
  createdAt: new Date(dbProject.created_at).getTime(),
  // Default to 4:3 if aspect_ratio column doesn't exist yet
  aspectRatio: (dbProject.aspect_ratio as AspectRatio) || '4:3',
  stagingStyle: (dbProject.staging_style as StagingStyle) || 'modern',
  uploadToken: dbProject.upload_token,
  isUploadActive: dbProject.is_upload_active ?? true,
  tenantUploadCount: dbProject.tenant_upload_count ?? 0,
  propertyType: dbProject.property_type ?? null,
  propertyAreaSqm: dbProject.property_area_sqm ?? null,
  propertyRooms: dbProject.property_rooms ?? null,
  propertyPrice: dbProject.property_price ?? null,
  propertyStatus: dbProject.property_status ?? null,
});

/**
 * Save hero overlay config for an image
 */
export const saveHeroOverlayConfig = async (
  imageId: string,
  config: HeroOverlayConfig | null
): Promise<void> => {
  const { error } = await supabase
    .from('images')
    .update({ hero_overlay_config: config })
    .eq('id', imageId);

  if (error) {
    throw new Error(`Hero-Overlay konnte nicht gespeichert werden: ${error.message}`);
  }
};

/**
 * Save hero image storage path for an image
 */
export const saveHeroImagePath = async (
  imageId: string,
  storagePath: string
): Promise<void> => {
  const { error } = await supabase
    .from('images')
    .update({ storage_path_hero: storagePath })
    .eq('id', imageId);

  if (error) {
    throw new Error(`Hero-Bild-Pfad konnte nicht gespeichert werden: ${error.message}`);
  }
};

/**
 * Clear hero image data from all other images in a project.
 * Called when a new hero image is set to ensure only one image has the badge.
 */
export const clearOtherHeroImages = async (
  projectId: string,
  keepImageId: string
): Promise<void> => {
  const { error } = await supabase
    .from('images')
    .update({ storage_path_hero: null, hero_overlay_config: null })
    .eq('project_id', projectId)
    .neq('id', keepImageId);

  if (error) {
    console.error('[Hero] Failed to clear old hero images:', error.message);
  }
};

// ============================================================================
// Workspace / Bild-Popup Additions (ImageInspectorModal & ProjectWorkspaceView)
// ============================================================================

/** Minimal images-row subset the Bild-Popup needs (self-contained fetch). */
export interface ImageDetailRow {
  id: string;
  storage_path: string | null;
  storage_path_empty: string | null;
  storage_path_staged: string | null;
  storage_path_branded: string | null;
  storage_path_branded_empty: string | null;
  room_type: string;
  custom_label: string | null;
  keep_kitchen: boolean | null;
}

/**
 * Fetch a single image row (subset of columns) for the inspector popup.
 */
export const fetchImageDetail = async (imageId: string): Promise<ImageDetailRow | null> => {
  const { data, error } = await supabase
    .from('images')
    .select('id, storage_path, storage_path_empty, storage_path_staged, storage_path_branded, storage_path_branded_empty, room_type, custom_label, keep_kitchen')
    .eq('id', imageId)
    .maybeSingle();

  if (error) {
    throw new Error(`Bild konnte nicht geladen werden: ${error.message}`);
  }

  return data as ImageDetailRow | null;
};

/**
 * Fetch all Empty/Staged history rows for an image (variant list of popup UND
 * Flow-v3-Varianten-Strip — beide müssen dieselbe Quelle/Reihenfolge nutzen).
 * Ordered ascending so "Leer A" is always the oldest empty variant; storage_path
 * als Tiebreaker, weil A/B-Varianten eines Aufrufs dieselbe created_at haben
 * (Pfad-Suffix _v0/_v1 sortiert sie korrekt).
 */
export const fetchImageHistoryEntries = async (
  imageId: string
): Promise<import('@/types').ImageHistoryEntry[]> => {
  const { data, error } = await supabase
    .from('image_history')
    .select('id, image_id, version_type, storage_path, created_at')
    .eq('image_id', imageId)
    .in('version_type', ['Empty', 'Staged'])
    .order('created_at', { ascending: true })
    .order('storage_path', { ascending: true });

  if (error) {
    throw new Error(`Verlauf konnte nicht geladen werden: ${error.message}`);
  }

  return (data ?? []) as import('@/types').ImageHistoryEntry[];
};

/**
 * Set a variant as the "current" one on the images row
 * (e.g. pick between Leer A/B — the current empty is used as staging base).
 */
export const setCurrentVariant = async (
  imageId: string,
  versionType: 'Empty' | 'Staged',
  storagePath: string
): Promise<void> => {
  const field = versionType === 'Empty' ? 'storage_path_empty' : 'storage_path_staged';
  const { error } = await supabase
    .from('images')
    .update({ [field]: storagePath })
    .eq('id', imageId);

  if (error) {
    throw new Error(`Variante konnte nicht übernommen werden: ${error.message}`);
  }
};

/**
 * Delete a single generated variant of an image:
 * removes the image_history row (if any), the storage file (best-effort) and —
 * if the variant was the current storage_path_empty/staged — promotes the
 * newest remaining variant of the same type (or NULL) on the images row.
 */
export const deleteImageVariant = async (params: {
  imageId: string;
  versionType: 'Empty' | 'Staged';
  storagePath: string;
  historyId: string | null;
}): Promise<void> => {
  const { imageId, versionType, storagePath, historyId } = params;

  if (historyId) {
    const { error } = await supabase
      .from('image_history')
      .delete()
      .eq('id', historyId);

    if (error) {
      throw new Error(`Variante konnte nicht gelöscht werden: ${error.message}`);
    }
  }

  // Best-effort storage cleanup
  if (storagePath) {
    const { error: storageError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .remove([storagePath]);
    if (storageError) {
      console.warn('[Variant] Storage deletion warning:', storageError.message);
    }
  }

  // If the deleted variant was current on the images row → promote or clear
  const field = versionType === 'Empty' ? 'storage_path_empty' : 'storage_path_staged';
  const { data: img, error: imgError } = await supabase
    .from('images')
    .select('storage_path_empty, storage_path_staged')
    .eq('id', imageId)
    .maybeSingle();

  if (imgError || !img) return;

  const currentPath = versionType === 'Empty' ? img.storage_path_empty : img.storage_path_staged;
  if (currentPath !== storagePath) return;

  const { data: remaining } = await supabase
    .from('image_history')
    .select('storage_path')
    .eq('image_id', imageId)
    .eq('version_type', versionType)
    .order('created_at', { ascending: false })
    .limit(1);

  const nextPath = remaining && remaining.length > 0 ? remaining[0].storage_path : null;

  const { error: updateError } = await supabase
    .from('images')
    .update({ [field]: nextPath })
    .eq('id', imageId);

  if (updateError) {
    throw new Error(`Variante konnte nicht aktualisiert werden: ${updateError.message}`);
  }
};

/**
 * Delete a whole image from the project including its history storage files.
 * History DB rows cascade with the images row; storage cleanup is best-effort.
 */
export const deleteImageWithHistory = async (imageId: string): Promise<void> => {
  const { data: historyRows } = await supabase
    .from('image_history')
    .select('storage_path')
    .eq('image_id', imageId);

  const historyPaths = (historyRows ?? [])
    .map((r) => r.storage_path)
    .filter((p): p is string => !!p);

  if (historyPaths.length > 0) {
    const { error: storageError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .remove(historyPaths);
    if (storageError) {
      console.warn('[Image] History storage deletion warning:', storageError.message);
    }
  }

  await deleteMultipleImages([imageId]);
};
